import { create } from 'zustand';
import { CareerData } from '../dto/EntityRepository';
import { CareerPeriodType } from '../enum/EnumRepository';
import type { ResourceScope, ResourceStatus } from '../util/scopedResource';

interface CareerListState {
  careers: CareerData[];
  listOwnerAui: string | null;
  listScope: ResourceScope | null;
  status: ResourceStatus;
  setCareers: (careers: CareerData[]) => void;
  beginCareerListFetch: (ownerAui: string, scope: ResourceScope) => void;
  setCareerList: (careers: CareerData[], ownerAui: string, scope: ResourceScope) => void;
  setCareerListError: () => void;
  updateCareerLocal: (id: string, updates: Partial<CareerData>) => void;
  setOnlyAccessValue: (id: string, isPublic: boolean) => void;
  afterCreateCareer: (career: CareerData) => void;
  markCareersSaved: (ids: string[]) => void;
  afterDeleteCareer: (id: string) => void;
  clearCareers: () => void;
}

const isSameCareer = (prev: CareerData, next: CareerData): boolean => {
  if (
    prev.id !== next.id ||
    prev.careerType !== next.careerType ||
    prev.periodType !== next.periodType ||
    prev.content !== next.content ||
    prev.contentEn !== next.contentEn ||
    prev.relatedUrl !== next.relatedUrl ||
    prev.isPublic !== next.isPublic ||
    prev.startYear !== next.startYear ||
    prev.startMonth !== next.startMonth ||
    prev.startDay !== next.startDay
  ) {
    return false;
  }
  if (prev.periodType === CareerPeriodType.PERIOD && next.periodType === CareerPeriodType.PERIOD) {
    return (
      prev.isCurrent === next.isCurrent &&
      prev.currentLabelOrig === next.currentLabelOrig &&
      prev.endYear === next.endYear &&
      prev.endMonth === next.endMonth &&
      prev.endDay === next.endDay
    );
  }
  return true;
};

const isSameCareerList = (prev: CareerData[], next: CareerData[]): boolean =>
  prev.length === next.length &&
  prev.every((career, index) => isSameCareer(career, next[index]));

export const useCareerListStore = create<CareerListState>((set) => ({
  careers: [],
  listOwnerAui: null,
  listScope: null,
  status: 'idle',
  setCareers: (careers) =>
    set(() => ({
      careers: careers.map((c) => ({ ...c, hasChanged: false })),
    })),
  beginCareerListFetch: (ownerAui, scope) => set((state) => {
    // 같은 owner+scope 재fetch: success 유지(SWR). 다르면 loading으로 숨김.
    if (
      state.listOwnerAui === ownerAui &&
      state.listScope === scope &&
      state.status === 'success'
    ) {
      return state;
    }
    return { status: 'loading' };
  }),
  setCareerList: (careers, ownerAui, scope) =>
    set((state) => {
      const normalized = careers.map((c) => ({ ...c, hasChanged: false }));
      if (
        state.status === 'success' &&
        state.listOwnerAui === ownerAui &&
        state.listScope === scope &&
        isSameCareerList(state.careers, normalized)
      ) {
        return state;
      }
      return {
        careers: normalized,
        listOwnerAui: ownerAui,
        listScope: scope,
        status: 'success',
      };
    }),
  setCareerListError: () => set({ status: 'error' }),
  setOnlyAccessValue: (id: string, isPublic: boolean) =>
    set(({ careers }) => ({
      careers: careers.map((c) => c.id === id ? { ...c, isPublic } : c),
    })),
  updateCareerLocal: (id, updates) =>
    set(({ careers }) => ({
      careers: careers.map((c) =>
        c.id !== id ? c : ({ ...c, ...updates, hasChanged: true } as CareerData),
      ),
    })),
  afterCreateCareer: (career) =>
    set(({ careers }) => ({
      careers: [{ ...career, hasChanged: false }, ...careers]
    })),
  markCareersSaved: (ids) =>
    set(({ careers }) => {
      const savedIds = new Set(ids);
      return {
        careers: careers.map((career) =>
          savedIds.has(career.id)
            ? { ...career, hasChanged: false }
            : career
        ),
      };
    }),
  afterDeleteCareer: (id) =>
    set(({ careers }) => ({
      careers: careers.filter((pi) => pi.id !== id)
    })),
  clearCareers: () => set((state) => {
    if (
      state.careers.length === 0 &&
      state.listOwnerAui === null &&
      state.listScope === null &&
      state.status === 'idle'
    ) {
      return state;
    }
    return {
      careers: [],
      listOwnerAui: null,
      listScope: null,
      status: 'idle',
    };
  }),
}));
